//DEV Core file to this Task
import { createContext, useContext } from 'react';

import { GrfqVenue } from '#/unified-rfqs/entities/products/venue';
import {
  Counterparty,
  isLiquidityProvider,
} from '#/unified-rfqs/entities/drfq/domain';

import venueView from './views/venue';
import productView from './views/product';
import auctionView, { AuctionView } from './views/auction';
import accountView, { AccountView } from './views/account';
import productShortcutView from './views/product-shortcut';
import strategyView from './views/strategy';
import strategyShortcutView from './views/strategy-shortcut';
import hedgeView from './views/hedge';

import { State } from './store';
import { FormOption } from './types';
import { createLegView, LegView } from './leg-view';
import { GreeksView, greeksView } from './views/greeks';
import { isHedgeLeg } from './leg-functions';
import {
  checkMultiProductStructure,
  coarsestQuantityPrecision,
  isMultiProductAllowed,
} from './multi-product';

export const ANON_RFQ_MIN_ALLOWED_COUNTERPARIES = 3;

export interface View {
  readonly venue: {
    readonly options: readonly FormOption<GrfqVenue['code']>[];
    readonly selectedOption: FormOption<GrfqVenue['code']>;
  };
  readonly product: {
    readonly options: readonly FormOption[];
    readonly selectedOption: FormOption;
  };

  readonly account: AccountView;
  readonly auction: AuctionView;

  readonly productShortcut: {
    readonly options: readonly FormOption<string>[];
    readonly selectedOption: FormOption<string> | null;
  };

  readonly strategy: {
    readonly options: readonly FormOption[];
    readonly selectedOption: FormOption | null;
  };

  readonly strategyShortcut: {
    readonly options: readonly (FormOption & {
      readonly isDisabled: boolean;
    })[];
    readonly selectedOption: FormOption | null;
  };

  readonly legs: readonly LegView[];

  readonly hedge: {
    readonly isDisabled: boolean;
    readonly errorMsg?: string;
    readonly submitMsg?: string;
  };

  readonly isValid: boolean;

  /**
   * Structure-level rule violation (e.g. legs spanning multiple products when
   * the multi-product feature does not allow it). Shown as a persistent
   * notice in the builder and blocks submission while present.
   */
  readonly structureError: string | undefined;

  readonly greeks: GreeksView;

  readonly counterpartiesError: string;
  readonly counterparties: State['counterparties'];
  readonly anonymous: boolean;
  readonly isOrderBook: boolean;

  readonly hiddenLimitOrder: {
    readonly enabled: boolean;
    readonly bidPrice: string;
    readonly askPrice: string;
    readonly showFields: boolean;
    readonly isValid: boolean;
  };
}

export const ViewCtx = createContext<View | null>(null);

ViewCtx.displayName = 'RfqBuilderView';

export function useRfqBuilderView() {
  const ctx = useContext(ViewCtx);
  if (ctx !== null) return ctx;
  throw new Error('Missing RfqBuilderProvider upwards in this tree.');
}

interface CreateViewOptions {
  readonly multiProductVenues?: readonly string[];
}

export function createView(state: State, options?: CreateViewOptions): View {
  const multiProductVenues = options?.multiProductVenues ?? [];
  const venue = venueView(state.venue, state.auction);
  const product = productView(state.productCode, state.venue, state.auction);
  const auction = auctionView(state.auction, state.productCode);
  const isOrderBook = auction.selectedOption.value === 'OB';
  const account = accountView(state.account, isOrderBook);
  const productShortcut = productShortcutView(state.productCode);
  const strategy = strategyView(
    state.productCode,
    state.strategyCode,
    state.venue,
  );
  const strategyShortcut = strategyShortcutView(
    state.productCode,
    state.strategyCode,
    state.venue,
  );
  const isCustomStrategy = strategy.selectedOption?.value === null;
  const quantityPrecisionOverride = coarsestQuantityPrecision(state.legs);
  const legs = state.legs.map((leg) =>
    createLegView(
      leg,
      state.instruments,
      state.venue,
      isCustomStrategy,
      isOrderBook,
      // The override is resolved across the strategy legs only, so it must
      // not be applied to a hedge leg: its quantity is its own product's,
      // not the package's, and rounding it to a precision derived from
      // products it isn't on would misreport it.
      isHedgeLeg(leg) ? undefined : quantityPrecisionOverride,
    ),
  );
  const hedge = hedgeView(state.strategyCode, state.legs, isOrderBook);
  const greeks = greeksView(state);

  const counterpartiesError = cpsValidation(
    auction,
    state.counterparties,
    state.anonymous,
  );

  const allowMultiProduct = isMultiProductAllowed(
    multiProductVenues,
    state.venue,
    state.auction,
  );
  const structureError = checkMultiProductStructure(
    state.legs,
    allowMultiProduct,
  );

  const hiddenLimitOrder = {
    enabled: state.hiddenLimitOrder.enabled,
    bidPrice: state.hiddenLimitOrder.bidPrice,
    askPrice: state.hiddenLimitOrder.askPrice,
    showFields: !isOrderBook,
    isValid: isValidHiddenLimitOrder(state.hiddenLimitOrder),
  };

  const isRfqValid =
    account.isValid &&
    legs.length > 0 &&
    legs.every((leg) => leg.isValid) &&
    counterpartiesError.length === 0 &&
    hiddenLimitOrder.isValid &&
    structureError == null;

  return {
    venue,
    product,
    account,
    auction,
    productShortcut,
    strategy,
    strategyShortcut,
    legs,
    hedge,
    isValid: isRfqValid,
    structureError,
    greeks,
    counterpartiesError,
    isOrderBook,
    anonymous: state.anonymous,
    counterparties: state.counterparties,
    hiddenLimitOrder,
  };
}

function cpsValidation(
  auction: AuctionView,
  counterparties: readonly Counterparty[],
  anonymous: boolean,
) {
  // Only applicable to RFQs
  if (auction.selectedOption.value !== 'RFQ') return '';

  if (counterparties.length === 0)
    return 'Please select one or more counterparties';

  const haveMinAllowedCounterparties =
    counterparties.filter((c) => isLiquidityProvider(c)).length <
    ANON_RFQ_MIN_ALLOWED_COUNTERPARIES;
  const shouldWarnAboutMinAllowedCounterparies =
    anonymous && haveMinAllowedCounterparties;
  if (shouldWarnAboutMinAllowedCounterparies)
    return `To send an Anonymous RFQ, please select at least ${ANON_RFQ_MIN_ALLOWED_COUNTERPARIES}x LPs`;

  return '';
}

function isValidHiddenLimitOrder(
  hiddenLimitOrder: State['hiddenLimitOrder'],
): boolean {
  // If disabled, always valid
  if (!hiddenLimitOrder.enabled) return true;

  // If enabled, at least one price must be provided and both must be valid
  const hasAtLeastOnePrice =
    hiddenLimitOrder.bidPrice !== '' || hiddenLimitOrder.askPrice !== '';
  const areBothPricesValid =
    isValidPriceOrEmpty(hiddenLimitOrder.bidPrice) &&
    isValidPriceOrEmpty(hiddenLimitOrder.askPrice);
  const isBidPriceGreaterThanOrEqualToAskPrice =
    hiddenLimitOrder.bidPrice !== '' &&
    hiddenLimitOrder.askPrice !== '' &&
    Number(hiddenLimitOrder.bidPrice) >= Number(hiddenLimitOrder.askPrice);

  return (
    hasAtLeastOnePrice &&
    areBothPricesValid &&
    !isBidPriceGreaterThanOrEqualToAskPrice
  );
}

function isValidPriceOrEmpty(price: string): boolean {
  if (price === '') return true;
  // Match positive numbers: integers or decimals with required digits after decimal
  // ^(\d+|\d*\.\d+)$ - matches "123" or optional digits + dot + required digits
  return /^(\d+|\d*\.\d+)$/u.test(price);
}
